import React from "react";
import { SQFT_TIERS, RESIDENTIAL_PLANS, SQFT_TIER_FOOTNOTE } from "@/data/maintenancePlans";
import { Star } from "lucide-react";

// Visual ordering of plans in the table follows the data file order:
// RoofWatch → Essential → Plus (Most Popular) → Reserve
const PLAN_ORDER = RESIDENTIAL_PLANS.map((p) => p.id);

export default function SqftPricingTable() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="sqft-pricing-table">
      <div className="px-6 py-5 border-b border-gray-200">
        <div className="br-eyebrow text-br-bronze">Square Footage Pricing</div>
        <h3 className="font-display text-2xl tracking-widest uppercase mt-2">Pricing by Home Size</h3>
        <p className="text-sm text-gray-600 mt-2">Each tier carries an initial inspection fee and per-plan monthly pricing with included crew-hour limits per approved claim.</p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-br-paper border-b border-gray-200">
            <tr className="text-left text-[10px] uppercase tracking-[0.25em] text-gray-600">
              <th className="px-4 py-3 font-bold">Tier</th>
              <th className="px-4 py-3 font-bold">Home Size</th>
              <th className="px-4 py-3 font-bold">Initial Inspection</th>
              {PLAN_ORDER.map((id) => {
                const plan = RESIDENTIAL_PLANS.find((p) => p.id === id);
                return (
                  <th key={id} className={`px-4 py-3 font-bold ${plan.highlight ? "bg-br-bronze/10 text-br-bronze" : ""}`}>
                    <div className="flex items-center gap-1">
                      {plan.highlight && <Star size={11} style={{ fill: "#b07c2c", color: "#b07c2c" }} />}
                      <span>{plan.name}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {SQFT_TIERS.map((tier) => (
              <tr key={tier.id} className="hover:bg-br-paper/40" data-testid={`sqft-tier-row-${tier.id}`}>
                <td className="px-4 py-3 align-top">
                  <div className="font-display tracking-widest uppercase text-xs text-br-bronze">{tier.label}</div>
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="text-sm font-semibold text-gray-900">{tier.range}</div>
                </td>
                <td className="px-4 py-3 align-top">
                  <div className="text-sm font-semibold text-gray-900">{tier.inspectionFeeLabel}</div>
                  {tier.isCustom && (
                    <div className="text-[11px] text-gray-500 mt-0.5 max-w-[12rem]">{tier.customNote}</div>
                  )}
                </td>
                {PLAN_ORDER.map((id) => {
                  const plan = RESIDENTIAL_PLANS.find((p) => p.id === id);
                  const cell = tier.plans[id];
                  return (
                    <td key={id} className={`px-4 py-3 align-top ${plan.highlight ? "bg-br-bronze/5" : ""}`}>
                      <div className="font-bold text-gray-900 tabular-nums">{cell.priceLabel}</div>
                      <div className="text-[11px] text-gray-600 mt-0.5">{cell.crewHours}</div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-gray-200 bg-br-paper/40 text-[11px] text-gray-600 leading-relaxed">
        {SQFT_TIER_FOOTNOTE}
      </div>
    </div>
  );
}
